<template>
  <PrimeDialog
    v-model:visible="visible"
    modal
    :header="null"
    :style="{ width: '85vw', maxWidth: '1000px' }"
    :breakpoints="{ '1199px': '90vw', '575px': '100vw' }"
    class="user-details-dialog"
    :closable="false"
    @hide="$emit('close')"
  >
    <template #header>
      <div class="header-container w-100 mt-n4 mx-0 border-radius-lg bg-gradient-success shadow-success p-3 d-flex justify-content-between align-items-center">
        <h5 class="text-white mb-0 font-weight-bolder">
          <i class="material-icons align-middle me-2">manage_accounts</i>
          Gerenciar Usuário: {{ details?.user?.full_name || '...' }}
        </h5>
        <button class="btn btn-link text-white p-0 m-0" @click="visible = false">
          <i class="material-icons">close</i>
        </button>
      </div>
    </template>
    <div v-if="loading" class="d-flex justify-content-center align-items-center p-5">
      <div class="spinner-border text-success" role="status">
        <span class="visually-hidden">Carregando...</span>
      </div>
    </div>

    <div v-else-if="details" class="user-details-content pt-4 pb-2 px-3">
      <div class="row">
        <!-- BLOCO 1: INFORMAÇÕES BÁSICAS -->
        <div class="col-md-4 mb-5">
          <div class="card h-100 border-0 shadow-lg">
            <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
              <div class="bg-gradient-dark shadow-dark border-radius-lg py-2 px-3">
                <h6 class="text-white text-capitalize m-0 text-sm">Dados do Usuário</h6>
              </div>
            </div>
            <div class="card-body p-3 pt-4">
              <ul class="list-group list-group-flush">
                <li class="list-group-item border-0 ps-0 text-xs d-flex align-items-center">
                  <i class="material-icons opacity-6 me-2 text-xs">fingerprint</i>
                  <strong class="text-dark">ID:</strong> &nbsp; #{{ details.user.id }}
                </li>
                <li class="list-group-item border-0 ps-0 text-xs d-flex align-items-start">
                  <i class="material-icons opacity-6 me-2 text-xs mt-1">email</i>
                  <div>
                    <strong class="text-dark">Email:</strong> <br/> {{ details.user.email }}
                  </div>
                </li>
                <li class="list-group-item border-0 ps-0 text-xs text-nowrap d-flex align-items-center">
                  <i class="material-icons opacity-6 me-2 text-xs">phone</i>
                  <strong class="text-dark">Telefone:</strong> &nbsp; {{ details.user.phone || '---' }}
                </li>
                <li class="list-group-item border-0 ps-0 text-xs d-flex align-items-center">
                  <i class="material-icons opacity-6 me-2 text-xs">badge</i>
                  <strong class="text-dark">Documento:</strong> &nbsp; {{ details.user.document || '---' }}
                </li>
                <li class="list-group-item border-0 ps-0 text-xs d-flex align-items-center">
                  <i class="material-icons opacity-6 me-2 text-xs">verified_user</i>
                  <strong class="text-dark">Status:</strong> &nbsp; 
                  <span :class="'badge badge-sm ' + (details.user.verified ? 'bg-gradient-success' : 'bg-gradient-warning')">
                    {{ details.user.verified ? 'Verificado' : 'Pendente' }}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- BLOCO 2: CARTEIRA DO SISTEMA -->
        <div class="col-md-4 mb-5">
          <div class="card h-100 border-0 shadow-lg">
            <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
              <div class="bg-gradient-info shadow-info border-radius-lg py-2 px-3 d-flex justify-content-between align-items-center">
                <h6 class="text-white text-capitalize m-0 text-sm">Carteira</h6>
                <div v-if="details.wallet.system_wallet" class="badge badge-xs bg-white text-info font-weight-bold p-1 px-2 border-radius-md" style="font-size: 0.6rem">ATIVA</div>
              </div>
            </div>
            <div class="card-body p-3 pt-4 text-center d-flex flex-column">
              <div v-if="details.wallet.system_wallet" class="wallet-active my-auto">
                 <div class="icon icon-lg icon-shape bg-gradient-info shadow-info text-center border-radius-xl mt-n4 mx-auto mb-3">
                  <i class="material-icons opacity-10">account_balance_wallet</i>
                </div>
                <h3 class="mb-0 font-weight-bolder text-dark">{{ formatCurrency(details.wallet.balance) }}</h3>
                <p class="text-xxs text-secondary text-uppercase font-weight-bold">Saldo total em conta</p>
              </div>
              <div v-else class="wallet-inactive my-auto py-2">
                <i class="material-icons text-secondary opacity-2" style="font-size: 3rem">no_accounts</i>
                <p class="text-xs text-secondary mt-2">Uso de carteira própria<br/><small>(Gateway externo)</small></p>
              </div>

              <div class="mt-4">
                <button 
                  class="btn btn-sm w-100 mb-0 border-radius-lg" 
                  :class="details.wallet.system_wallet ? 'btn-outline-danger' : 'bg-gradient-info text-white'"
                  @click="confirmWalletToggle"
                >
                  {{ details.wallet.system_wallet ? 'Desativar Carteira' : 'Ativar Carteira' }}
                </button>
                <router-link
                  :to="{
                    path: '/wallet-applications',
                    query: { search: details.user.email },
                  }"
                  class="btn btn-sm btn-outline-info w-100 mt-2 mb-0 border-radius-lg"
                >
                  Ver solicitação de qualificação
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- BLOCO 3: RESUMO DE CAMPANHAS -->
        <div class="col-md-4 mb-5">
          <div class="card h-100 border-0 shadow-lg">
            <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
              <div class="bg-gradient-warning shadow-warning border-radius-lg py-2 px-3">
                <h6 class="text-white text-capitalize m-0 text-sm">Campanhas</h6>
              </div>
            </div>
            <div class="card-body p-3 pt-4">
              <div class="d-flex justify-content-between mb-2">
                <span class="text-xs font-weight-bold">Total:</span>
                <span class="text-xs font-weight-bolder text-dark">{{ details.campaigns.total }}</span>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span class="text-xs font-weight-bold">Ativas:</span>
                <span class="badge badge-xs bg-gradient-success" style="padding: 0.35em 0.65em">{{ details.campaigns.active || 0 }}</span>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span class="text-xs font-weight-bold">Finalizadas:</span>
                <span class="badge badge-xs bg-dark" style="padding: 0.35em 0.65em">{{ details.campaigns.finished || 0 }}</span>
              </div>
              <div class="d-flex justify-content-between">
                <span class="text-xs font-weight-bold">Pausadas:</span>
                <span class="badge badge-xs bg-gradient-warning" style="padding: 0.35em 0.65em">{{ details.campaigns.paused || 0 }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- BLOCO 4: GESTÃO DE PLANO -->
      <div class="row">
        <div class="col-12">
          <div class="card border-0 shadow-lg overflow-visible">
            <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
              <div class="bg-gradient-dark shadow-dark border-radius-lg py-3 px-4 d-flex justify-content-between align-items-center">
                <h6 class="text-white text-capitalize m-0 font-weight-bolder">Gerenciamento Administrativo de Plano</h6>
                <div v-if="planForm.use_plan" :class="getPlanBadgeClass()" class="p-2 px-3">
                  {{ getPlanStatusLabel() }}
                </div>
              </div>
            </div>
            <div class="card-body p-4 pt-5">
              <div class="row align-items-start bg-gray-100 border-radius-lg p-3 mx-1">
                <div class="col-md-4 mb-md-0 mb-3">
                  <div class="d-flex align-items-center">
                    <ToggleSwitch v-model="planForm.use_plan" class="custom-switch" />
                    <div class="ms-3">
                      <label class="form-label mb-0 text-sm font-weight-bolder text-dark">Ativar Plano de Isenção</label>
                      <p class="text-xxs text-secondary mb-0">Habilita isenção de taxas de publicação</p>
                    </div>
                  </div>
                </div>

                <div class="col-md-3 mb-md-0 mb-3" v-if="planForm.use_plan">
                  <div class="d-flex flex-column">
                    <label class="text-xxs font-weight-bolder text-uppercase text-secondary mb-1">Tipo de Plano</label>
                    <select class="form-select form-select-sm border border-radius-sm ps-2" v-model="planForm.plan_active" style="border: 1px solid #d2d6da !important;">
                      <option value="MENSAL">Mensal (Starter)</option>
                      <option value="ANUAL">Anual</option>
                      <option value="VITALICIO">Vitalício (Diamond)</option>
                    </select>
                  </div>
                </div>

                <div class="col-md-3 mb-md-0 mb-3" v-if="planForm.use_plan && (planForm.plan_active === 'MENSAL' || planForm.plan_active === 'ANUAL')">
                  <div class="d-flex flex-column">
                    <label class="text-xxs font-weight-bolder text-uppercase text-secondary mb-1">Data de Vencimento</label>
                    <input type="date" class="form-control form-control-sm border border-radius-sm ps-2" v-model="planForm.plan_expiration" style="border: 1px solid #d2d6da !important;">
                  </div>
                </div>

                <div class="col-md-2 ms-auto text-end">
                  <button 
                    class="btn bg-gradient-primary btn-sm mb-0 px-4 border-radius-lg shadow-primary mt-2" 
                    @click="savePlanSettings"
                    :disabled="saving"
                  >
                    <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                    SALVAR
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="col-12 mb-4 mt-5">
            <div class="card border-0 shadow-lg overflow-visible">
              <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                <div class="bg-gradient-success shadow-success border-radius-lg py-3 px-4 d-flex justify-content-between align-items-center">
                  <h6 class="text-white text-capitalize m-0 font-weight-bolder">
                    Gerenciamento Administrativo do Remarketing
                  </h6>
                  <span
                    class="badge badge-sm p-2 px-3"
                    :class="remarketingForm.active ? 'bg-white text-success' : 'bg-gradient-secondary'"
                  >
                    {{ remarketingForm.active ? 'ATIVO' : 'INATIVO' }}
                  </span>
                </div>
              </div>
              <div class="card-body p-4 pt-5">
                <div class="row align-items-start bg-gray-100 border-radius-lg p-3 mx-1">
                  <div class="col-md-5 mb-md-0 mb-3">
                    <div class="d-flex align-items-center">
                      <ToggleSwitch v-model="remarketingForm.active" class="custom-switch custom-switch-success" />
                      <div class="ms-3">
                        <label class="form-label mb-0 text-sm font-weight-bolder text-dark">
                          Ativar Remarketing WhatsApp
                        </label>
                        <p class="text-xxs text-secondary mb-0">
                          Liberação manual para pagamentos feitos por fora
                        </p>
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4 mb-md-0 mb-3" v-if="remarketingForm.active">
                    <div class="d-flex flex-column">
                      <label class="text-xxs font-weight-bolder text-uppercase text-secondary mb-1">
                        Data e hora de vencimento
                      </label>
                      <input
                        type="datetime-local"
                        class="form-control form-control-sm border border-radius-sm ps-2"
                        v-model="remarketingForm.expires_at"
                        style="border: 1px solid #d2d6da !important;"
                      />
                      <small class="text-xxs text-secondary mt-1">
                        WhatsApp: {{ details.remarketing?.instance_status || 'DISCONNECTED' }}
                      </small>
                    </div>
                  </div>

                  <div class="col-md-3 ms-auto text-end">
                    <button
                      class="btn bg-gradient-success btn-sm mb-0 px-4 border-radius-lg shadow-success mt-2"
                      @click="saveRemarketingSettings"
                      :disabled="savingRemarketing"
                    >
                      <span v-if="savingRemarketing" class="spinner-border spinner-border-sm me-2"></span>
                      SALVAR
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-12 mb-4 mt-5">
            <div class="card border-0 shadow-lg overflow-visible">
              <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                <div class="bg-gradient-primary shadow-primary border-radius-lg py-3 px-4 d-flex justify-content-between align-items-center">
                  <h6 class="text-white text-capitalize m-0 font-weight-bolder">
                    Gerenciamento Administrativo da Academy
                  </h6>
                  <span
                    class="badge badge-sm p-2 px-3"
                    :class="academyForm.active ? 'bg-white text-primary' : 'bg-gradient-secondary'"
                  >
                    {{ academyForm.active ? 'ATIVO' : 'INATIVO' }}
                  </span>
                </div>
              </div>
              <div class="card-body p-4 pt-5">
                <div class="row align-items-start bg-gray-100 border-radius-lg p-3 mx-1">
                  <div class="col-md-4 mb-md-0 mb-3">
                    <div class="d-flex align-items-center">
                      <ToggleSwitch v-model="academyForm.active" class="custom-switch custom-switch-primary" />
                      <div class="ms-3">
                        <label class="form-label mb-0 text-sm font-weight-bolder text-dark">
                          Liberar Academy
                        </label>
                        <p class="text-xxs text-secondary mb-0">
                          Liberação manual ou complemento ao plano Starter
                        </p>
                      </div>
                    </div>
                  </div>

                  <div class="col-md-3 mb-md-0 mb-3" v-if="academyForm.active">
                    <div class="d-flex align-items-center h-100">
                      <ToggleSwitch v-model="academyForm.lifetime" class="custom-switch" />
                      <div class="ms-3">
                        <label class="form-label mb-0 text-sm font-weight-bolder text-dark">Vitalício</label>
                      </div>
                    </div>
                  </div>

                  <div class="col-md-3 mb-md-0 mb-3" v-if="academyForm.active && !academyForm.lifetime">
                    <div class="d-flex flex-column">
                      <label class="text-xxs font-weight-bolder text-uppercase text-secondary mb-1">
                        Data e hora de vencimento
                      </label>
                      <input
                        type="datetime-local"
                        class="form-control form-control-sm border border-radius-sm ps-2"
                        v-model="academyForm.expires_at"
                        style="border: 1px solid #d2d6da !important;"
                      />
                    </div>
                  </div>

                  <div class="col-md-2 ms-auto text-end">
                    <button
                      class="btn bg-gradient-primary btn-sm mb-0 px-4 border-radius-lg shadow-primary mt-2"
                      @click="saveAcademySettings"
                      :disabled="savingAcademy"
                    >
                      <span v-if="savingAcademy" class="spinner-border spinner-border-sm me-2"></span>
                      SALVAR
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-12 mb-4">
            <div class="card my-4">
              <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                <div class="bg-gradient-info shadow-info border-radius-lg py-3 px-4">
                  <h6 class="text-white text-capitalize m-0 font-weight-bolder">Domínio personalizado</h6>
                </div>
              </div>
              <div class="card-body p-4 pt-4">
                <div class="row align-items-end">
                  <div class="col-md-8 mb-3 mb-md-0">
                    <label class="text-xxs font-weight-bolder text-uppercase text-secondary mb-1">Domínio personalizado</label>
                    <input
                      type="text"
                      class="form-control form-control-sm border border-radius-sm ps-2"
                      v-model="domainForm"
                      placeholder="ex: meudominio.com.br"
                      style="border: 1px solid #d2d6da !important;"
                    />
                    <p class="text-xxs text-secondary mb-0 mt-2">
                      Após salvar, ainda é preciso adicionar o domínio personalizado na Vercel (manual) e o cliente apontar o DNS.
                    </p>
                  </div>
                  <div class="col-md-4 text-md-end">
                    <button
                      class="btn bg-gradient-info btn-sm mb-0 px-4 border-radius-lg shadow-info"
                      @click="saveDomain"
                      :disabled="savingDomain"
                    >
                      <span v-if="savingDomain" class="spinner-border spinner-border-sm me-2"></span>
                      SALVAR DOMÍNIO
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ConfirmDialog />
  </PrimeDialog>
</template>

<script>
import PrimeDialog from 'primevue/dialog';
import ToggleSwitch from 'primevue/toggleswitch';
import ConfirmDialog from 'primevue/confirmdialog';
import UsersHttp from '@/http/UsersHttp';

export default {
  name: 'UserDetailsModal',
  components: {
    PrimeDialog,
    ToggleSwitch,
    ConfirmDialog
  },
  props: {
    isOpen: Boolean,
    userId: Number
  },
  data() {
    return {
      visible: false,
      loading: false,
      saving: false,
      savingRemarketing: false,
      savingAcademy: false,
      savingDomain: false,
      details: null,
      domainForm: '',
      planForm: {
        use_plan: false,
        plan_active: 'MENSAL',
        plan_expiration: ''
      },
      remarketingForm: {
        active: false,
        expires_at: ''
      },
      academyForm: {
        active: false,
        lifetime: false,
        expires_at: ''
      }
    };
  },
  watch: {
    isOpen(val) {
      this.visible = val;
      if (val && this.userId) {
        this.fetchDetails();
      }
    }
  },
  methods: {
    async fetchDetails() {
      this.loading = true;
      try {
        const response = await UsersHttp.getAdminUserDetails(this.userId);
        this.details = response.data;
        
        // Populate form
        this.planForm.use_plan = this.details.plan.use_plan;
        this.planForm.plan_active = this.details.plan.plan_active || 'MENSAL';
        this.planForm.plan_expiration = this.details.plan.plan_expiration ? this.details.plan.plan_expiration.split('T')[0] : '';
        this.remarketingForm.active = !!this.details.remarketing?.active;
        this.remarketingForm.expires_at = this.toDateTimeLocal(this.details.remarketing?.expires_at);
        this.academyForm.active = !!this.details.academy?.admin_active;
        this.academyForm.lifetime = !!this.details.academy?.standalone_lifetime;
        this.academyForm.expires_at = this.toDateTimeLocal(this.details.academy?.standalone_expires_at);
        this.domainForm = this.details.user?.domain || '';
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível carregar os detalhes do usuário.',
          life: 3000
        });
        this.$emit('close');
      } finally {
        this.loading = false;
      }
    },
    async saveDomain() {
      this.savingDomain = true;
      try {
        const value = (this.domainForm || '').trim();
        await UsersHttp.updateAdminUserDomain(this.userId, value || null);
        this.$toast.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: value ? 'Domínio atualizado.' : 'Domínio removido.',
          life: 3000
        });
        await this.fetchDetails();
        this.$emit('updated');
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: error?.error || error?.message || error.response?.data?.error || 'Erro ao salvar domínio.',
          life: 3500
        });
      } finally {
        this.savingDomain = false;
      }
    },
    async savePlanSettings() {
      if (this.planForm.use_plan && (this.planForm.plan_active === 'MENSAL' || this.planForm.plan_active === 'ANUAL') && !this.planForm.plan_expiration) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Atenção',
          detail: 'Defina uma data de vencimento para o plano mensal ou anual.',
          life: 3000
        });
        return;
      }

      this.$confirm.require({
        message: 'Tem certeza que deseja aplicar estas alterações de plano?',
        header: 'Confirmar Alterações',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sim, Salvar',
        rejectLabel: 'Cancelar',
        acceptClass: 'p-button-primary border-radius-lg',
        accept: async () => {
          this.saving = true;
          try {
            const payload = {
              system_wallet: this.details.wallet.system_wallet,
              use_plan: this.planForm.use_plan,
              plan_active: this.planForm.plan_active,
              plan_expiration: this.planForm.plan_active === 'VITALICIO' ? null : this.planForm.plan_expiration
            };

            await UsersHttp.updateUserProfile(this.userId, payload);
            
            this.$toast.add({
              severity: 'success',
              summary: 'Sucesso',
              detail: 'Configurações de plano atualizadas.',
              life: 3000
            });
            
            this.fetchDetails();
            this.$emit('updated');
          } catch (error) {
            this.$toast.add({
              severity: 'error',
              summary: 'Erro',
              detail: error.response?.data?.error || 'Erro ao salvar alterações.',
              life: 3000
            });
          } finally {
            this.saving = false;
          }
        }
      });
    },
    toDateTimeLocal(value) {
      if (!value) return '';
      const raw = String(value).trim().replace(' ', 'T');
      return raw.length >= 16 ? raw.slice(0, 16) : raw;
    },
    async saveRemarketingSettings() {
      if (this.remarketingForm.active && !this.remarketingForm.expires_at) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Atenção',
          detail: 'Defina a data e hora de vencimento do Remarketing.',
          life: 3000
        });
        return;
      }
      this.savingRemarketing = true;
      try {
        await UsersHttp.updateAdminRemarketing(this.userId, {
          active: this.remarketingForm.active,
          expires_at: this.remarketingForm.active ? this.remarketingForm.expires_at : null
        });
        this.$toast.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: this.remarketingForm.active
            ? 'Remarketing liberado manualmente.'
            : 'Remarketing desativado.',
          life: 3000
        });
        await this.fetchDetails();
        this.$emit('updated');
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: error.response?.data?.error || 'Erro ao atualizar o Remarketing.',
          life: 3000
        });
      } finally {
        this.savingRemarketing = false;
      }
    },
    async saveAcademySettings() {
      if (this.academyForm.active && !this.academyForm.lifetime && !this.academyForm.expires_at) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Atenção',
          detail: 'Defina a data de vencimento ou marque acesso vitalício.',
          life: 3000
        });
        return;
      }
      this.savingAcademy = true;
      try {
        await UsersHttp.updateAdminAcademy(this.userId, {
          active: this.academyForm.active,
          lifetime: this.academyForm.lifetime,
          expires_at: this.academyForm.active && !this.academyForm.lifetime ? this.academyForm.expires_at : null
        });
        this.$toast.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: this.academyForm.active ? 'Academy liberada.' : 'Academy desativada.',
          life: 3000
        });
        await this.fetchDetails();
        this.$emit('updated');
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: error.response?.data?.error || 'Erro ao atualizar a Academy.',
          life: 3000
        });
      } finally {
        this.savingAcademy = false;
      }
    },
    async confirmWalletToggle() {
      const action = this.details.wallet.system_wallet ? 'desativar' : 'ativar';
      
      this.$confirm.require({
        message: `Tem certeza que deseja ${action} a carteira? O usuário passará a receber ${action === 'ativar' ? 'pela carteira do sistema' : 'por gateway próprio'}.`,
        header: 'Confirmar Alteração de Carteira',
        icon: action === 'ativar' ? 'pi pi-wallet' : 'pi pi-exclamation-circle',
        acceptLabel: `Sim, ${action === 'ativar' ? 'Ativar' : 'Desativar'}`,
        rejectLabel: 'Cancelar',
        acceptClass: action === 'ativar' ? 'p-button-info border-radius-lg' : 'p-button-danger border-radius-lg',
        accept: async () => {
          this.toggleWallet();
        }
      });
    },
    async toggleWallet() {
      this.saving = true;
      try {
        const payload = {
          system_wallet: !this.details.wallet.system_wallet,
          use_plan: this.planForm.use_plan,
          plan_active: this.planForm.plan_active,
          plan_expiration: this.planForm.plan_expiration
        };

        await UsersHttp.updateUserProfile(this.userId, payload);
        
        this.$toast.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Carteira atualizada com sucesso.',
          life: 3000
        });
        this.fetchDetails();
        this.$emit('updated');
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível alterar o status da carteira.',
          life: 3000
        });
      } finally {
        this.saving = false;
      }
    },
    formatCurrency(value) {
      if (!value) return 'R$ 0,00';
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    },
    getPlanBadgeClass() {
      const status = this.details?.plan?.status;
      if (this.details?.plan?.plan_active === 'VITALICIO') return 'badge badge-sm bg-gradient-primary';
      if (status === 'ACTIVE') return 'badge badge-sm bg-gradient-success';
      if (status === 'EXPIRED') return 'badge badge-sm bg-gradient-danger';
      return 'badge badge-sm bg-gradient-secondary';
    },
    getPlanStatusLabel() {
      if (this.details?.plan?.plan_active === 'VITALICIO') return 'DIAMOND / VITALÍCIO';
      if (this.details?.plan?.plan_active === 'ANUAL' && this.details?.plan?.status === 'ACTIVE') return 'ANUAL / ATIVO';
      if (this.details?.plan?.plan_active === 'ANUAL') return 'ANUAL / VENCIDO';
      if (this.details?.plan?.status === 'ACTIVE') return 'STARTER / ATIVO';
      if (this.details?.plan?.status === 'EXPIRED') return 'PLANO VENCIDO';
      return 'SEM PLANO';
    }
  }
};
</script>

<style scoped>
.user-details-dialog :deep(.p-dialog-header) {
  padding: 0 !important;
  background: transparent !important;
  border: none !important;
}

.user-details-dialog :deep(.p-dialog-content) {
  padding: 0 !important;
  border-radius: 12px;
  overflow: visible;
}

.header-container {
  z-index: 3;
}

.user-details-content {
  background-color: #f0f2f5;
  border-radius: 12px;
}

.card .card-header .bg-gradient-dark {
  background-image: linear-gradient(195deg, #42424a 0%, #191919 100%);
}

.card .card-header .bg-gradient-info {
  background-image: linear-gradient(195deg, #49a3f1 0%, #1a73e8 100%);
}

.card .card-header .bg-gradient-warning {
  background-image: linear-gradient(195deg, #ffa726 0%, #fb8c00 100%);
}

.card .card-header .bg-gradient-success {
  background-image: linear-gradient(195deg, #66bb6a 0%, #43a047 100%);
}

.badge-xs {
  font-size: 0.7rem;
  padding: 0.35rem 0.5rem;
}

.form-switch .form-check-input {
  width: 2.5rem;
  height: 1.25rem;
  cursor: pointer;
}

.form-select-sm, .form-control-sm {
  border: 1px solid #d2d6da !important;
  padding: 0.5rem 0.75rem !important;
}

.shadow-dark {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(64, 64, 64, 0.4) !important;
}

.shadow-info {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(0, 188, 212, 0.4) !important;
}

.shadow-warning {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(255, 152, 0, 0.4) !important;
}

.bg-gradient-primary {
  background-image: linear-gradient(195deg, #EC407A 0%, #D81B60 100%);
}

.shadow-primary {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(233, 30, 99, 0.4) !important;
}

.custom-switch :deep(.p-toggleswitch-slider) {
  background: #d2d6da;
}

.custom-switch :deep(.p-toggleswitch-input:checked + .p-toggleswitch-slider) {
  background: #D81B60;
}

.custom-switch-success :deep(.p-toggleswitch-input:checked + .p-toggleswitch-slider) {
  background: #43a047;
}

.bg-gray-100 {
  background-color: #f8f9fa !important;
}

.input-group-static {
  border-bottom: 2px solid #d2d6da;
  transition: border-color 0.3s ease;
}

.input-group-static:focus-within {
  border-bottom: 2px solid #D81B60;
}

.form-select-sm {
  background-color: transparent !important;
}

.overflow-visible {
  overflow: visible !important;
}

:deep(.swal2-container) {
  z-index: 106000 !important;
}

body .swal2-container {
  z-index: 106000 !important;
}
</style>
